import axios, { AxiosResponse } from "axios";

import { getToken } from "apiCommandsExecutor/token";

/*
* Адрес API — одно место на весь кабинет. Пустая строка означает тот же origin, что у самого
* фронта: так работают и прод, и стенды. Для локальной разработки раскомментируйте нужную строку.
* */
export const API_HOST = "";
// export const API_HOST = "http://localhost:5004";
// export const API_HOST = "https://dev-my.restify.one";

export const API_URL_REST_ENDPOINT = `${API_HOST}/api/rest/warehouse/v1`;

const REST_REQUEST_FAILED_MESSAGE = "Запрос не выполнен";

type RestExecutionStatus = "Finished" | "Failed";

export interface RestApiResponse<T> {
    executionStatus: RestExecutionStatus;
    data: T | null;
    message?: string;
}

const buildRestUrl = (path: string) => `${API_URL_REST_ENDPOINT}${path}`;

const buildRestAuthHeaders = () => ({
    AuthToken: getToken(),
});

const restAxiosConfig = {
    validateStatus: () => true,
} as const;

const getFinishedData = <T>(
    response: RestApiResponse<T> | undefined
): T | null | undefined => {
    if (response?.executionStatus === "Finished") {
        return response.data ?? null;
    }

    return undefined;
};

export const restGet = async <ResponseType>(
    path: string,
    params?: Record<string, unknown>
): Promise<ResponseType | null> => {
    const result: AxiosResponse<RestApiResponse<ResponseType>> = await axios.get(
        buildRestUrl(path),
        {
            ...restAxiosConfig,
            headers: buildRestAuthHeaders(),
            params,
        }
    );

    return getFinishedData(result.data) ?? null;
};

export const restPut = async <ResponseType>(
    path: string,
    body?: unknown
): Promise<ResponseType | null> => {
    const result: AxiosResponse<RestApiResponse<ResponseType>> = await axios.put(
        buildRestUrl(path),
        body,
        {
            ...restAxiosConfig,
            headers: buildRestAuthHeaders(),
        }
    );

    const data = getFinishedData(result.data);

    if (data !== undefined) {
        return data;
    }

    throw new Error(result.data?.message || REST_REQUEST_FAILED_MESSAGE);
};

export const restPost = async <ResponseType>(
    path: string,
    body?: unknown
): Promise<ResponseType | null> => {
    const result: AxiosResponse<RestApiResponse<ResponseType>> = await axios.post(
        buildRestUrl(path),
        body,
        {
            ...restAxiosConfig,
            headers: buildRestAuthHeaders(),
        }
    );

    const data = getFinishedData(result.data);

    if (data !== undefined) {
        return data;
    }

    throw new Error(result.data?.message || REST_REQUEST_FAILED_MESSAGE);
};

export const restDelete = async <ResponseType>(
    path: string
): Promise<ResponseType | null> => {
    const result: AxiosResponse<RestApiResponse<ResponseType>> =
        await axios.delete(buildRestUrl(path), {
            ...restAxiosConfig,
            headers: buildRestAuthHeaders(),
        });

    const data = getFinishedData(result.data);

    if (data !== undefined) {
        return data;
    }

    throw new Error(result.data?.message || REST_REQUEST_FAILED_MESSAGE);
};

/*
* Тело загрузки: у всех ручек кабинета один файл в поле `file`. Content-Type не задаём —
* axios сам проставит его с boundary, а вручную собранный заголовок boundary потеряет.
* */
export const fileBody = (file: File) => {
    const body = new FormData();
    body.append("file", file);

    return body;
};

// Ручки, отдающие сам файл, а не конверт RestApiResponse: там разворачивать нечего.
export const restGetBlob = async (path: string): Promise<Blob> => {
    const result = await axios.get<Blob>(buildRestUrl(path), {
        ...restAxiosConfig,
        headers: buildRestAuthHeaders(),
        responseType: "blob",
    });

    if (result.status >= 400) {
        throw new Error(REST_REQUEST_FAILED_MESSAGE);
    }

    return result.data;
};
